/**
 * 아웃라인의 「축약」 — 절마다 한 줄 요약에 쓸 재료(수·서브쿼리 종류·항목 미리보기). 문구는 화면이 번역한다(여기는 숫자·원문 조각만).
 */
const collapse = (s) => String(s || '').replace(/\s+/g, ' ').trim();
const clip = (s, n = 28) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** 항목 하나의 짧은 이름 — SELECT 는 별칭(없으면 식), FROM 은 테이블(+별칭), 조건은 식 */
export function itemPreview(src, item, type, child) {
    if (child) return child.label || null;
    let text = collapse(src.slice(item.start, item.end));
    if (type === 'FROM') {
        text = text.replace(/^((INNER|LEFT|RIGHT|FULL|OUTER|CROSS|NATURAL)\s+)*(JOIN|APPLY)\s+/i, '');
        text = text.replace(/\s+(ON|USING)\s[\s\S]*$/i, '');
        return clip(text, 32);
    }
    if (type === 'SELECT') {
        const m = text.match(/(?:\bAS\s+|\s)("[^"]+"|[A-Za-z_\u0080-￿][\w$#\u0080-￿]*)$/i);
        if (m && !/^(END|NULL)$/i.test(m[1]) && text.length > m[1].length + 1) return clip(m[1].replace(/"/g, ''), 28);
        return clip(text, 28);
    }
    return clip(text, 36);
}

/** 절 요약 재료 */
export function clauseSummary(src, clause) {
    const kinds = {};
    for (const ch of clause.children) kinds[ch.kind] = (kinds[ch.kind] || 0) + 1;
    // 항목이 곧 서브쿼리인 경우(인라인뷰·스칼라 — 조인 접두어 뒤 첫머리가 괄호)만 그 이름을 쓴다. 조건 속 EXISTS 는 식 그대로
    const childByItem = (it) => {
        const whole = src.slice(it.start, it.end);
        const head = whole.replace(/^((INNER|LEFT|RIGHT|FULL|OUTER|CROSS|NATURAL)\s+)*(JOIN|APPLY)\s+/i, '');
        const offset = it.start + (whole.length - head.length);
        return clause.children.find(ch => ch.outerStart === offset && ch.outerEnd <= it.end && ch.label);
    };
    const previews = clause.items.slice(0, 4).map(it => itemPreview(src, it, clause.type, childByItem(it))).filter(Boolean);
    return { count: clause.items.length, kinds, previews, more: Math.max(0, clause.items.length - 4) };
}

/** 접힌 질의의 한 줄 — [['SELECT', 3], ['FROM', 2], …] */
export function querySummary(q) {
    if (q.branches.length) return [['BRANCHES', q.branches.length]];
    return q.clauses.map(c => [c.type, c.items.length]);
}

/** 질의 본문의 첫 줄(툴팁·미리보기) */
export function firstLine(src, q, n = 60) {
    return clip(collapse(src.slice(q.start, q.end)), n);
}

/**
 * 접힌 질의의 테이블 이름 — FROM 항목마다 테이블 이름(별칭·조인 조건 없이), 인라인뷰는 그 이름. UNION 이면 갈래들의 것을 차례로(같은 이름은 한 번).
 * 접으면 절 줄이 숨어 「무엇을 읽는 질의인가」 가 안 보였다(2026-10-27 사용자 요청).
 */
export function queryTables(src, q, max = 4) {
    const out = [];
    const visit = (node) => {
        if (node.branches.length) { node.branches.forEach(visit); return; }
        const from = node.clauses.find(c => c.type === 'FROM');
        if (!from) return;
        const { previews } = clauseSummary(src, from);
        for (const p of previews) {
            const child = from.children.find(ch => ch.label === p);
            const name = child ? p : p.split(/\s+/)[0];
            if (name && !out.includes(name)) out.push(name);
        }
    };
    visit(q);
    return { names: out.slice(0, max), more: Math.max(0, out.length - max) };
}
